import { useEffect, useState } from 'react';
import { audioEngine } from '@/audio/engine/AudioEngine';
import { PITCH_CLASS_NAMES } from '@/data/tuning';
import { useAudioState } from '@/hooks/useAudio';
import type { OnsetEvent } from '@/types/audio';
import './DebugPanel.css';

const PITCH_LABELS = PITCH_CLASS_NAMES;

/**
 * Development-only audio diagnostics.
 *
 * Rendered only when `import.meta.env.DEV` and the URL carries `?debug=1`, so
 * it can never appear for a real user — and it is the only place raw DSP
 * numbers are allowed to be shown at all.
 */
export function DebugPanel() {
  // Read once at mount rather than in an effect: the query string is a static
  // external value, and setting state from an effect just to read it causes an
  // extra render for nothing.
  const [enabled] = useState(
    () => import.meta.env.DEV && new URLSearchParams(window.location.search).get('debug') === '1',
  );
  const [onsets, setOnsets] = useState<OnsetEvent[]>([]);
  const state = useAudioState();

  useEffect(() => {
    if (!enabled) return;
    return audioEngine.onOnset((onset) => {
      setOnsets((current) => [onset, ...current].slice(0, 6));
    });
  }, [enabled]);

  if (!enabled) return null;

  const frame = state.frame;
  const chroma = frame?.chroma;
  const maxIndex = chroma ? argMax(chroma) : -1;

  return (
    <aside className="debug" aria-hidden="true">
      <p className="debug__row">
        status <b>{state.status}</b> · rate <b>{state.contextSampleRate}</b>
      </p>
      <p className="debug__row">
        rms <b>{frame?.rms.toFixed(4) ?? '—'}</b> · snr <b>{frame?.snrDb.toFixed(1) ?? '—'} dB</b> ·
        tonal <b>{frame?.tonalness.toFixed(2) ?? '—'}</b>
        {frame?.clipped && <span className="debug__clip"> CLIP</span>}
      </p>
      <p className="debug__row">
        floor <b>{state.calibration?.profile.floorDb.toFixed(1) ?? '—'} dB</b> · quality{' '}
        <b>{state.calibration?.quality ?? '—'}</b>
      </p>
      <p className="debug__row">
        source <b>{frame?.source?.kind ?? '—'}</b> · guitar conf{' '}
        <b>{frame?.source?.guitarConfidence.toFixed(2) ?? '—'}</b>
      </p>
      {chroma && (
        <div className="debug__chroma">
          {Array.from(chroma).map((value, index) => (
            <span key={index} className="debug__bar" title={PITCH_LABELS[index]}>
              <span
                className={`debug__bar-fill${index === maxIndex ? ' debug__bar-fill--max' : ''}`}
                style={{ height: `${Math.round(value * 100)}%` }}
              />
              <em>{PITCH_LABELS[index]}</em>
            </span>
          ))}
        </div>
      )}
      <p className="debug__row">
        onsets:{' '}
        {onsets.map((onset) => `${onset.time.toFixed(2)}(${onset.strength.toFixed(1)})`).join(' ') || '—'}
      </p>
      {state.appliedConstraints && (
        <p className="debug__row debug__row--small">
          agc <b>{String(state.appliedConstraints.autoGainControl)}</b> · ns{' '}
          <b>{String(state.appliedConstraints.noiseSuppression)}</b> · aec{' '}
          <b>{String(state.appliedConstraints.echoCancellation)}</b>
        </p>
      )}
    </aside>
  );
}

function argMax(values: Float32Array): number {
  let best = 0;
  for (let i = 1; i < values.length; i++) if (values[i] > values[best]) best = i;
  return best;
}
